/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable max-len */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { debounce } from 'lodash';
import AllGames from 'src/conponents/Home/AllGames';
import { DEFAULT_CONFIGS } from 'src/constants';
import useSearchQueryParams from 'src/hooks/useSearchQueryParams';
import './index.scss';
import { useLanguageContext } from 'src/context/languageContext';
import useHistory from 'src/hooks/useHistory';
import useSort from 'src/hooks/useSort';
import useTheme from 'src/hooks/useTheme';
import PropTypes from 'prop-types';
// Brand detection kept for potential future use
import 'src/conponents/Shared/Header';
import { useMarket } from 'src/context/marketContext';
import {
  topGamesByMarket,
  campaignSuggestions,
  campaignTranslations,
  platformStats,
} from 'src/data/market-intelligence';
import ACTIVE_RECTANGLE_GAMES from 'src/data/active-games';
import ZH_TW_ONLY_GAMES from 'src/data/zh-tw-games';
import GAME_RANKINGS from 'src/data/game-rankings';
import MARKET_RANKINGS from 'src/data/market-rankings';
import {
  capitalize,
  MAX_WIN_RANGES,
  fetchPortfolio,
  loadCachedPortfolio,
  buildDisplayList,
  computeFilterOptions,
  countActiveFilters,
  processGames,
} from 'src/helpers/gameFilters';
import GameSort from './GameSort';


function HomeContainer() {
  const { i18n } = useLanguageContext();
  useHistory();
  const [searchTerm, setSearchTerm] = useState('');
  // Hydrate from localStorage cache so the page never starts empty when the
  // API is briefly down. Live fetch overwrites this on success.
  const [originalData, setOriginalData] = useState(() => loadCachedPortfolio() || []);
  const { sortBy, sortDirection } = useSort();
  useTheme();
  const {
    ui, market: headerMarket, lang, version,
  } = useMarket();
  const [searchFocused, setSearchFocused] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Filter state
  const [filterType, setFilterType] = useState('all');
  const [filterVol, setFilterVol] = useState('all');
  const [filterMaxWin, setFilterMaxWin] = useState('all');
  const [filterFamily, setFilterFamily] = useState('all');
  const [filterProvider, setFilterProvider] = useState('all');
  const [filterMarket, setFilterMarket] = useState(
    headerMarket === 'global' ? 'all' : headerMarket,
  );

  useEffect(() => {
    setFilterMarket(headerMarket === 'global' ? 'all' : headerMarket);
  }, [headerMarket]);

  const l = useSearchQueryParams('l', DEFAULT_CONFIGS.language);
  const values = useSearchQueryParams();
  const [filterBy] = useState(values.get('filter_by') || 'all');

  // Merge zh-TW-only preview games when that language is active.
  // Done at display time only — never written to cache.
  const gamesForDisplay = useMemo(() => {
    if (lang !== 'zh-tw' && lang !== 'zh') return originalData;
    const existingCodes = new Set(originalData.map((g) => g.code));
    const extras = ZH_TW_ONLY_GAMES.filter((g) => !existingCodes.has(g.code));
    return [...extras, ...originalData];
  }, [originalData, lang]);

  // Filter pill options come from the unfiltered/unsearched portfolio so the
  // pills don't shrink as the user types or applies other filters.
  const baselineGames = useMemo(
    () => processGames(gamesForDisplay, { filterBy }),
    [gamesForDisplay, filterBy],
  );
  const filterOptions = useMemo(
    () => computeFilterOptions(baselineGames),
    [baselineGames],
  );

  const filters = useMemo(() => ({
    type: filterType,
    vol: filterVol,
    maxWin: filterMaxWin,
    family: filterFamily,
    provider: filterProvider,
  }), [filterType, filterVol, filterMaxWin, filterFamily, filterProvider]);

  const displayGames = useMemo(() => buildDisplayList({
    games: gamesForDisplay,
    searchTerm,
    filterBy,
    filters,
    sort: { sortBy, sortDirection },
    marketRankings: MARKET_RANKINGS,
    globalRankings: GAME_RANKINGS,
    market: filterMarket,
  }), [
    gamesForDisplay, searchTerm, filterBy, filters,
    sortBy, sortDirection, filterMarket,
  ]);

  const activeFilterCount = countActiveFilters({
    ...filters,
    market: filterMarket,
  });

  const clearFilters = () => {
    setFilterType('all');
    setFilterVol('all');
    setFilterMaxWin('all');
    setFilterFamily('all');
    setFilterProvider('all');
    setFilterMarket('all');
  };

  const debouncedSearch = useMemo(
    () => debounce((value) => setSearchTerm(value), 300),
    [],
  );
  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);
  const handleSearchGame = (e) => debouncedSearch(e.target.value);

  // Sequence number guards against an older fetch overwriting a newer one
  // when the locale changes mid-flight.
  const fetchSeq = useRef(0);
  // Skip the spinner if the cache already gave us something to show.
  const [loading, setLoading] = useState(originalData.length === 0);
  const fetchData = async (params) => {
    const seq = fetchSeq.current + 1;
    fetchSeq.current = seq;
    const games = await fetchPortfolio({
      params, activeGameCodes: ACTIVE_RECTANGLE_GAMES,
    });
    if (seq !== fetchSeq.current) return;
    setOriginalData(games);
    setLoading(false);
  };

  useEffect(() => {
    if (i18n?.locale) fetchData({ t: 'test', l });
    else setLoading(false);
  }, [i18n?.locale]);



  const scrollToGames = () => {
    const el = document.getElementById('games');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className={`showcase ${loading ? '--loading' : ''}`}>
      {loading && (
        <div className="showcase-loader">
          <div className="loader-spinner">
            <div className="spinner-ring" />
            <div className="spinner-ring" />
            <div className="spinner-ring" />
          </div>
          <span>Loading portfolio...</span>
        </div>
      )}

      {!loading && (
        <>
          {/* ═══ HERO ═══ */}
          <section className="hero">
            <div className="hero__bg" />
            <div className="hero__content">
              {version === 'sweepstakes' && (
                <span className="hero__badge">
                  &#127873; {ui.sweepstakesMode}
                </span>
              )}
              <h1 className="hero__title">
                {ui.heroTitle}
                <br />
                <span className="hero__accent">{ui.heroAccent}</span>
              </h1>
              <p className="hero__subtitle">
                {ui.heroSub}
              </p>
              <div className="hero__stats">
                <div className="hero__stat"><span className="hero__stat-value notranslate">{originalData.length || platformStats.totalGames}</span><span className="hero__stat-label">{ui.statGames}</span></div>
                <div className="hero__stat"><span className="hero__stat-value notranslate">{platformStats.markets}</span><span className="hero__stat-label">{ui.statMarkets}</span></div>
                <div className="hero__stat"><span className="hero__stat-value notranslate">{platformStats.operators}</span><span className="hero__stat-label">{ui.statOperators}</span></div>
                <div className="hero__stat"><span className="hero__stat-value notranslate">{platformStats.uptime}</span><span className="hero__stat-label">{ui.statUptime}</span></div>
              </div>
              <div className="hero__actions">
                <button type="button" className="hero__btn hero__btn--primary" onClick={scrollToGames}>
                  {ui.exploreGames}
                </button>
              </div>
            </div>
            {originalData.length > 0 && (
              <div className="hero__visual">
                {originalData.filter((g) => g.preview).slice(0, 5).map((g, i) => (
                  <div key={g.code} className={`hero__game hero__game--${i}`}>
                    <img src={g.preview} alt={g.name} loading="eager" fetchPriority="high" />
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* ═══ TOP PERFORMING ═══ */}
          {topGamesByMarket[headerMarket] && (
            <section className="top-performing">
              <h2 className="section-title">
                {ui.topPerforming}
              </h2>
              <p className="section-sub">
                {ui.topPerformingSub}
              </p>
              <div className="top-grid">
                {topGamesByMarket[headerMarket]
                  .slice(0, 5)
                  .map((entry) => {
                    const game = originalData.find(
                      (g) => g.code === entry.code,
                    );
                    return (
                      <div
                        key={entry.code}
                        className="top-card"
                      >
                        <span className="top-card__rank">
                          #{entry.rank}
                        </span>
                        {game?.preview && (
                          <div className="top-card__img">
                            <img
                              src={game.preview}
                              alt={game?.name || entry.code}
                              loading="lazy"
                            />
                          </div>
                        )}
                        <div className="top-card__body">
                          <span className="top-card__name">
                            {game?.name || entry.code}
                          </span>
                          <span className="top-card__stats">
                            {entry.highlight}
                          </span>
                          {entry.insight && (
                            <span className="top-card__insight">
                              {entry.insight}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </section>
          )}

          {/* ═══ CAMPAIGNS ═══ */}
          {(campaignTranslations[lang]?.[headerMarket] || campaignSuggestions[headerMarket]) && (
            <section className="campaigns">
              <h2 className="section-title">
                {ui.campaigns}
              </h2>
              <p className="section-sub">
                {ui.campaignsSub}
              </p>
              <div className="campaign-grid">
                {(campaignTranslations[lang]?.[headerMarket] || campaignSuggestions[headerMarket]).map((c) => (
                  <div
                    key={c.title}
                    className="campaign-card"
                  >
                    <span className="campaign-card__type">
                      {c.type}
                    </span>
                    <h3 className="campaign-card__title">
                      {c.title}
                    </h3>
                    <p className="campaign-card__desc">
                      {c.desc}
                    </p>
                    {c.story && (
                      <div className="campaign-card__story">
                        <span className="campaign-card__story-label">
                          {ui.operatorStory}
                        </span>
                        <p>{c.story}</p>
                      </div>
                    )}
                    <div className="campaign-card__games">
                      {c.games.map((code) => {
                        const g = originalData.find(
                          (x) => x.code === code,
                        );
                        return g?.preview ? (
                          <img
                            key={code}
                            src={g.preview}
                            alt={g.name}
                            title={g.name}
                          />
                        ) : null;
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* ═══ GAMES PORTFOLIO ═══ */}
          <section className="portfolio" id="games">
            <div className="portfolio__header">
              <div>
                <h2 className="portfolio__title">{ui.gamePortfolio}</h2>
                <p className="portfolio__sub" data-testid="portfolio-count">
                  <span data-testid="portfolio-count-num">{displayGames.length}</span>{' '}
                  {ui.gamesAvailable}
                </p>
              </div>
            </div>

            {/* Search */}
            <div className={`portfolio__search ${searchFocused ? 'focused' : ''}`}>
              <SearchIcon />
              <input
                type="text"
                placeholder={ui.searchGames}
                onChange={handleSearchGame}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                data-testid="portfolio-search"
              />
            </div>

            {/* ── ADVANCED FILTERS ── */}
            <div className="filters">
              <div className="filters__bar">
                <button
                  type="button"
                  className={`filters__toggle ${filtersOpen ? 'active' : ''}`}
                  onClick={() => setFiltersOpen(!filtersOpen)}
                >
                  <FilterIcon />
                  <span>{ui.filters}</span>
                  {activeFilterCount > 0 && (
                    <span className="filters__count">{activeFilterCount}</span>
                  )}
                </button>

                {activeFilterCount > 0 && (
                  <button type="button" className="filters__clear" onClick={clearFilters}>
                    {ui.clearAll}
                  </button>
                )}

                {/* Active chips */}
                <div className="filters__chips">
                  {filterType !== 'all' && <Chip label={filterType} onRemove={() => setFilterType('all')} />}
                  {filterVol !== 'all' && <Chip label={`${capitalize(filterVol)} Vol`} onRemove={() => setFilterVol('all')} />}
                  {filterMaxWin !== 'all' && <Chip label={MAX_WIN_RANGES.find((r) => r.key === filterMaxWin)?.label} onRemove={() => setFilterMaxWin('all')} />}
                  {filterFamily !== 'all' && <Chip label={capitalize(filterFamily)} onRemove={() => setFilterFamily('all')} />}
                  {filterMarket !== 'all' && <Chip label={`Market: ${capitalize(filterMarket)}`} onRemove={() => setFilterMarket('all')} />}
                </div>

                <div className="filters__sort-wrap">
                  <GameSort />
                </div>
              </div>

              {filtersOpen && (
                <div className="filters__panel">
                  {/* Type — hidden when the portfolio has only one type (slots-only) */}
                  {filterOptions.types.length > 1 && (
                    <FilterGroup label={ui.gameType}>
                      <PillOption active={filterType === 'all'} onClick={() => setFilterType('all')}>All</PillOption>
                      {filterOptions.types.map((tp) => (
                        <PillOption key={tp} active={filterType === tp} onClick={() => setFilterType(tp)}>{capitalize(tp)}</PillOption>
                      ))}
                    </FilterGroup>
                  )}

                  {/* Volatility */}
                  <FilterGroup label={ui.volatility}>
                    <PillOption active={filterVol === 'all'} onClick={() => setFilterVol('all')}>All</PillOption>
                    {filterOptions.vols.map((v) => (
                      <PillOption key={v} active={filterVol === v} onClick={() => setFilterVol(v)}>{capitalize(v)}</PillOption>
                    ))}
                  </FilterGroup>

                  {/* Max Win */}
                  <FilterGroup label={ui.maxWin}>
                    {MAX_WIN_RANGES.map((r) => (
                      <PillOption key={r.key} active={filterMaxWin === r.key} onClick={() => setFilterMaxWin(r.key)}>{r.label}</PillOption>
                    ))}
                  </FilterGroup>

                  {/* Family */}
                  {filterOptions.families.length > 0 && (
                    <FilterGroup label={ui.gameFamily}>
                      <PillOption active={filterFamily === 'all'} onClick={() => setFilterFamily('all')}>All</PillOption>
                      {filterOptions.families.map((f) => (
                        <PillOption key={f} active={filterFamily === f} onClick={() => setFilterFamily(f)}>{capitalize(f)}</PillOption>
                      ))}
                    </FilterGroup>
                  )}

                  {/* Market */}
                  <FilterGroup label={ui.market}>
                    <PillOption active={filterMarket === 'all'} onClick={() => setFilterMarket('all')}>All</PillOption>
                    <PillOption active={filterMarket === 'brazil'} onClick={() => setFilterMarket('brazil')}>Brasil</PillOption>
                    <PillOption active={filterMarket === 'mexico'} onClick={() => setFilterMarket('mexico')}>Mexico</PillOption>
                    <PillOption active={filterMarket === 'asia'} onClick={() => setFilterMarket('asia')}>Asia</PillOption>
                    <PillOption active={filterMarket === 'europe'} onClick={() => setFilterMarket('europe')}>Europe</PillOption>
                  </FilterGroup>

                </div>
              )}
            </div>

            {/* Grid */}
            <AllGames games={displayGames} />

            {displayGames.length === 0 && (
              <div className="portfolio__empty" data-testid="portfolio-empty">
                <p>{ui.noGamesFound}</p>
                {activeFilterCount > 0 && (
                  <button type="button" className="filters__clear" onClick={clearFilters}>Clear all filters</button>
                )}
              </div>
            )}
          </section>

          {/* ═══ WHY SECTION ═══ */}
          <section className="why" id="about">
            <h2 className="why__title">
              {ui.whyTitle}
            </h2>
            <p className="why__sub">
              {ui.whySub}
            </p>
            <div className="why__grid">
              <FeatureCard stat="1" statSuffix=" API" title={ui.singleInteg} desc={ui.singleIntegDesc} />
              <FeatureCard stat="50" statSuffix="+" title={ui.currencies} desc={ui.currenciesDesc} />
              <FeatureCard stat="97" statSuffix="%" title={ui.avgRtp} desc={ui.avgRtpDesc} />
              <FeatureCard stat="&lt;50" statSuffix="ms" title={ui.latency} desc={ui.latencyDesc} />
              <FeatureCard stat="99.9" statSuffix="%" title={ui.uptimeTitle} desc={ui.uptimeDesc} />
              <FeatureCard stat="48" statSuffix="h" title={ui.goLive} desc={ui.goLiveDesc} />
            </div>
          </section>

          {/* ═══ FOOTER ═══ */}
          <footer className="site-footer">
            <div className="site-footer__bottom">
              <span className="site-footer__copy">
                &copy; {new Date().getFullYear()} {ui.copyright}
              </span>
              <span className="site-footer__updated">
                {ui.lastUpdated}
              </span>
            </div>
          </footer>

        </>
      )}
    </div>
  );
}

// ── Sub-components ──

function FilterGroup({ label, children }) {
  return (
    <div className="filter-group">
      <span className="filter-group__label">{label}</span>
      <div className="filter-group__options">{children}</div>
    </div>
  );
}
FilterGroup.propTypes = { label: PropTypes.string, children: PropTypes.node };

function PillOption({ active, onClick, children }) {
  return (
    <button type="button" className={`fpill ${active ? 'active' : ''}`} onClick={onClick}>
      {children}
    </button>
  );
}
PillOption.propTypes = { active: PropTypes.bool, onClick: PropTypes.func, children: PropTypes.node };

function Chip({ label, onRemove }) {
  return (
    <span className="filter-chip">
      {label}
      <button type="button" onClick={onRemove} aria-label="Remove filter">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
      </button>
    </span>
  );
}
Chip.propTypes = { label: PropTypes.string, onRemove: PropTypes.func };

function FeatureCard({ stat, statSuffix, title, desc }) {
  return (
    <div className="feature-card">
      <div className="feature-card__stat notranslate">
        <span className="feature-card__num">{stat}</span>
        <span className="feature-card__suffix">{statSuffix}</span>
      </div>
      <h3 className="feature-card__title">{title}</h3>
      <p className="feature-card__desc">{desc}</p>
    </div>
  );
}
FeatureCard.propTypes = {
  stat: PropTypes.string, statSuffix: PropTypes.string,
  title: PropTypes.string, desc: PropTypes.string,
};

function SearchIcon() {
  return (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>);
}

function FilterIcon() {
  return (<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="6" x2="20" y2="6" /><line x1="8" y1="12" x2="16" y2="12" /><line x1="11" y1="18" x2="13" y2="18" /></svg>);
}

export default HomeContainer;
